/**
 * O protocolo entre o carregador do Web Chat (página do cliente) e o aplicativo do iframe (origem Nuklie).
 *
 * Envelope versionado: `{ channel, version, type, payload }`. Os dois lados só aceitam uma mensagem que
 * venha da JANELA certa (`event.source`), da ORIGEM certa (`event.origin`), no canal certo e na versão
 * certa. O conteúdo sozinho não prova nada — qualquer script da página pode chamar `postMessage`.
 *
 * O que NUNCA viaja por aqui: o token do visitante, ids internos, conteúdo de mensagem. O iframe é quem
 * fala com a API; a página só fica sabendo que algo aconteceu.
 *
 * ## Identidade (Fase 7)
 *
 * O único segredo que atravessa é o token de IDENTIDADE, e só no sentido página → iframe: o backend do
 * software hospedeiro o assinou para o usuário logado, e ele vale minutos. O iframe o troca pelo token do
 * visitante e o esquece. Nada de identidade volta para a página além de `identity_changed { authenticated }`.
 *
 * ## Contexto (Fase 8)
 *
 * Também só página → iframe: o contexto declarado (`context.claimed.set`) e o token de contexto assinado
 * pelo backend (`context.verified.set`). O iframe nunca devolve contexto — nem o que a página mandou.
 */

export const WEBCHAT_CHANNEL = 'nuklie-chat';
export const WEBCHAT_PROTOCOL_VERSION = 1;

/** De onde a identidade vem, do ponto de vista do iframe. Nunca o token em si. */
export type IdentitySource = 'none' | 'token' | 'callback';

/** Carregador → iframe. */
export interface HostToFrame {
  /**
   * Resposta ao `ready`: a partir daqui o iframe pode iniciar a sessão.
   *
   * `identity: 'token'` avisa que um `identity.set` vem logo em seguida; `'callback'`, que o iframe pode
   * pedir um com `identity.request` quando precisar.
   */
  /**
   * `proactive: true` quer dizer "este iframe foi preparado para avaliar regras, e ninguém pediu o chat".
   *
   * Com ele e `open: false`, o app NÃO abre sessão: só busca configuração e regras. É o que impede que
   * cada página vista de cada site crie um `WebchatVisitor` — a decisão central da Fase 12.
   */
  init: { locale: string | null; open: boolean; resetSession: boolean; identity?: IdentitySource; contextCallback?: boolean; proactive?: boolean };
  open: Record<string, never>;
  close: Record<string, never>;
  /** "Este navegador não é mais aquele visitante": apaga o token e volta ao estado vazio. */
  reset: Record<string, never>;
  /** Um token de identidade assinado pelo backend do hospedeiro (`init` com `identityToken`, ou `identify`). */
  'identity.set': { token: string };
  /** A resposta a `identity.request`: o token que `getIdentityToken` devolveu, ou por que não veio. */
  'identity.response': { request_id: string; token?: string; error?: 'unavailable' | 'failed' };
  /** Logout no hospedeiro (`reset`): o iframe esquece visitante e identidade. Mesmo efeito de `reset`. */
  'identity.clear': Record<string, never>;
  /** O retrato atual da página, declarado pelo JavaScript dela. Substitui o anterior. */
  'context.claimed.set': { context: Record<string, unknown> };
  /** `clearContext`: sem contexto nesta conversa até a página mandar outro. */
  'context.claimed.clear': Record<string, never>;
  /** Um token de contexto assinado pelo backend do hospedeiro. */
  'context.verified.set': { token: string };
  /** A resposta a `context.request`. */
  'context.response': { request_id: string; token?: string; error?: 'unavailable' | 'failed' };
  'host.visibility': { visible: boolean };
  'host.viewport': { mobile: boolean };
  /**
   * A página em que a pessoa está agora — só o caminho, a busca e o título.
   *
   * Existe para as regras proativas de `page_view` e `time_on_page`. O carregador o manda no início e a
   * cada navegação que ele consegue perceber sem tocar no `history` do site: `popstate` e a Navigation
   * API quando o navegador a tem. Uma SPA que troque de tela sem nenhuma das duas continua descrevendo
   * a rota por `setContext({ route })`, que é o caminho da Fase 8 e já existia.
   *
   * Nunca a URL inteira com query: o caminho basta para a regra e não arrasta identificador de campanha
   * nem token que alguém tenha posto na barra de endereço.
   */
  'host.route': { path: string; title: string | null };
  /**
   * Um evento do software hospedeiro — `NuklieChat("track", "checkout.started")`.
   *
   * O nome é limitado e o payload é pequeno e DECLARADO: ele decide aparência, nunca autorização. Ver
   * `prepareTrackEvent`.
   */
  'host.track': { event: string; payload: Record<string, unknown> | null };
  /** A pessoa clicou no balão proativo. O carregador desenha; quem conta é o iframe. */
  'proactive.clicked': Record<string, never>;
  /** A pessoa fechou o balão. Conta como "já mostrei": é o que faz o intervalo começar daqui. */
  'proactive.dismissed': Record<string, never>;
}

/** Iframe → carregador. */
export interface FrameToHost {
  ready: Record<string, never>;
  resize: { height: number };
  unread_count: { count: number };
  conversation_started: Record<string, never>;
  /** Só metadado: nunca o texto. */
  message_received: { id: string; direction: 'inbound'; sent_at: string | null };
  conversation_closed: Record<string, never>;
  error: { code: string };
  request_open: Record<string, never>;
  request_close: Record<string, never>;
  /** O iframe precisa de um token de identidade novo: o carregador chama `getIdentityToken`. */
  'identity.request': { request_id: string };
  /** O iframe precisa de um token de contexto novo (o anterior venceu antes de haver conversa). */
  'context.request': { request_id: string };
  /** Só se há usuário verificado. Nunca quem. */
  identity_changed: { authenticated: boolean };
  /**
   * Uma regra proativa venceu a disputa: o carregador deve agir.
   *
   * O balão é desenhado pelo CARREGADOR, e não aqui dentro, por um motivo prático: enquanto ninguém
   * abriu o chat o iframe tem tamanho zero e fica atrás do botão. Um balão desenhado nele não teria
   * onde aparecer.
   *
   * O texto vem pronto e é inserido como TEXTO — ver `applyProactive` no carregador.
   */
  'proactive.show': { rule_id: string; action: 'show_teaser' | 'highlight_launcher' | 'open_chat'; text: string; cta: string | null };
  /** A regra deixou de valer (mudou de página, o chat abriu): tire o balão da tela. */
  'proactive.clear': Record<string, never>;
}

export interface Envelope<T extends string = string, P = unknown> {
  channel: typeof WEBCHAT_CHANNEL;
  version: typeof WEBCHAT_PROTOCOL_VERSION;
  type: T;
  payload: P;
}

export function envelope<M, K extends keyof M & string>(type: K, payload: M[K]): Envelope<K, M[K]> {
  return { channel: WEBCHAT_CHANNEL, version: WEBCHAT_PROTOCOL_VERSION, type, payload };
}

/**
 * O envelope, se a mensagem for mesmo nossa e vier de quem se espera. `null` para todo o resto.
 *
 * `expectedSource` e `expectedOrigin` são obrigatórios: sem eles não existe mensagem confiável.
 */
export function readEnvelope<M>(
  event: MessageEvent,
  expectedSource: MessageEventSource | null | undefined,
  expectedOrigin: string | null | undefined,
  allowed: ReadonlyArray<keyof M & string>,
): { type: keyof M & string; payload: M[keyof M] } | null {
  if (!expectedSource || !expectedOrigin) return null;
  if (event.source !== expectedSource || event.origin !== expectedOrigin) return null;

  const data = event.data as Partial<Envelope> | null;

  if (!data || typeof data !== 'object') return null;
  if (data.channel !== WEBCHAT_CHANNEL || data.version !== WEBCHAT_PROTOCOL_VERSION) return null;
  if (typeof data.type !== 'string' || !(allowed as ReadonlyArray<string>).includes(data.type)) return null;

  const payload = data.payload && typeof data.payload === 'object' ? data.payload : {};

  return { type: data.type as keyof M & string, payload: payload as M[keyof M] };
}

export const HOST_TO_FRAME_TYPES: ReadonlyArray<keyof HostToFrame> = [
  'init', 'open', 'close', 'reset', 'host.visibility', 'host.viewport', 'host.route', 'host.track',
  'proactive.clicked', 'proactive.dismissed',
  'identity.set', 'identity.response', 'identity.clear',
  'context.claimed.set', 'context.claimed.clear', 'context.verified.set', 'context.response',
];

export const FRAME_TO_HOST_TYPES: ReadonlyArray<keyof FrameToHost> = [
  'ready', 'resize', 'unread_count', 'conversation_started', 'message_received', 'conversation_closed', 'error', 'request_open', 'request_close',
  'identity.request', 'identity_changed', 'context.request',
  'proactive.show', 'proactive.clear',
];

/** O limite do token de identidade — o mesmo da API. */
export const IDENTITY_TOKEN_MAX = 8192;

/** O limite do token de contexto (o `ctx` vai dentro) — o mesmo da API. */
export const CONTEXT_TOKEN_MAX = 48 * 1024;

/** O limite do contexto declarado, em bytes de JSON — o mesmo de `HostContextSanitizer`. */
export const CONTEXT_MAX_BYTES = 32 * 1024;

export const CONTEXT_KEYS = ['route', 'display', 'entities', 'metadata'] as const;

/**
 * Checagem de FORMA do contexto declarado, antes de atravessar o `postMessage`: objeto simples, só as
 * chaves da v1, serializável em JSON e dentro do tamanho. O conteúdo (limites, links, chaves perigosas)
 * quem decide é o backend — `HostContextSanitizer` —, e esta função não tenta repetir aquilo.
 *
 * Devolve uma cópia JSON (sem funções, sem protótipo) ou o motivo da recusa.
 */
export function prepareClaimedContext(raw: unknown): { context: Record<string, unknown> } | { error: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { error: 'setContext precisa de um objeto.' };

  const proto = Object.getPrototypeOf(raw);
  if (proto !== Object.prototype && proto !== null) return { error: 'setContext precisa de um objeto simples.' };

  const desconhecidas = Object.keys(raw).filter(k => !(CONTEXT_KEYS as readonly string[]).includes(k));
  if (desconhecidas.length > 0) return { error: `setContext aceita só ${CONTEXT_KEYS.join(', ')} (recebeu ${desconhecidas.join(', ')}).` };

  let json: string;
  try {
    json = JSON.stringify(raw);
  } catch {
    return { error: 'setContext precisa de dados serializáveis em JSON.' };
  }

  if (new TextEncoder().encode(json).length > CONTEXT_MAX_BYTES) return { error: 'setContext passou de 32 KB.' };

  return { context: JSON.parse(json) as Record<string, unknown> };
}

/** Três partes base64url: tem forma de JWT. Quem confere de verdade é a API. */
export function looksLikeJwt(valor: unknown, max = IDENTITY_TOKEN_MAX): valor is string {
  return typeof valor === 'string' && valor.length > 0 && valor.length <= max
    && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/.test(valor);
}

/** Uma origem `http(s)://host[:porta]` bem formada, ou `null`. */
export function parseOrigin(valor: string | null | undefined): string | null {
  if (!valor) return null;

  try {
    const url = new URL(valor);

    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.origin === 'null' || url.origin !== valor.replace(/\/$/, '')) return null;

    return url.origin;
  } catch {
    return null;
  }
}

/** O limite do nome de um evento do hospedeiro — o mesmo de `ProactiveVocabulary`. */
export const TRACK_EVENT_MAX = 64;

/** `checkout.started`, `proposal.error`: letras, números, ponto, hífen e sublinhado. */
export const TRACK_EVENT_PATTERN = /^[a-zA-Z0-9._-]{1,64}$/;

/** O payload de um `track` é pequeno de propósito: ele decide aparência, não carrega dados. */
export const TRACK_PAYLOAD_MAX_BYTES = 2 * 1024;

export const TRACK_PAYLOAD_MAX_KEYS = 20;

/**
 * Chaves que nunca atravessam: as mesmas que `HostContextSanitizer` recusa no servidor.
 *
 * Um `{"__proto__": {...}}` vindo do site do cliente e colocado num objeto do nosso código é poluição de
 * protótipo — e aqui o objeto atravessaria um `postMessage` para dentro do NOSSO iframe.
 */
export const TRACK_DANGEROUS_KEYS = ['__proto__', 'prototype', 'constructor'];

/**
 * Valida e copia o que `NuklieChat("track", ...)` recebeu.
 *
 * ## O payload é DECLARADO, nunca autoridade
 *
 * Ele vem do JavaScript da página, que qualquer pessoa com o console aberto controla. Serve para decidir
 * se um balão aparece — e nada além disso. Não identifica ninguém, não autoriza nada, não vira entrada
 * de ferramenta. É a mesma regra do contexto declarado da Fase 8, e vale aqui pelo mesmo motivo.
 *
 * ## Um nível, valores simples
 *
 * Aninhamento livre convidaria a mandar o objeto de domínio inteiro do hospedeiro para dentro do nosso
 * iframe. Uma condição de regra lê `metadata.<chave>` com um nível; mais do que isso não teria quem
 * lesse, e teria quem vazasse.
 */
export function prepareTrackEvent(
  name: unknown,
  payload: unknown,
): { event: string; payload: Record<string, unknown> | null } | { error: string } {
  if (typeof name !== 'string' || !TRACK_EVENT_PATTERN.test(name)) {
    return { error: 'track precisa de um nome como "checkout.started" (letras, números, ponto, hífen e sublinhado).' };
  }

  if (payload === undefined || payload === null) return { event: name, payload: null };

  if (typeof payload !== 'object' || Array.isArray(payload)) return { error: 'O segundo argumento de track precisa ser um objeto.' };

  const proto = Object.getPrototypeOf(payload);
  if (proto !== Object.prototype && proto !== null) return { error: 'O segundo argumento de track precisa ser um objeto simples.' };

  const limpo: Record<string, unknown> = Object.create(null);
  let chaves = 0;

  for (const [chave, valor] of Object.entries(payload)) {
    if (TRACK_DANGEROUS_KEYS.includes(chave.toLowerCase())) return { error: `track não aceita a chave "${chave}".` };
    if (++chaves > TRACK_PAYLOAD_MAX_KEYS) return { error: `track aceita no máximo ${TRACK_PAYLOAD_MAX_KEYS} campos.` };

    const tipo = typeof valor;

    // Um nível, valores simples. Ver o docblock.
    if (valor !== null && tipo !== 'string' && tipo !== 'number' && tipo !== 'boolean') {
      return { error: `track aceita texto, número, booleano ou nulo — "${chave}" não é.` };
    }

    limpo[chave] = valor;
  }

  let json: string;
  try {
    json = JSON.stringify(limpo);
  } catch {
    return { error: 'track precisa de dados serializáveis em JSON.' };
  }

  if (new TextEncoder().encode(json).length > TRACK_PAYLOAD_MAX_BYTES) return { error: 'track passou de 2 KB.' };

  // `JSON.parse` devolve um objeto sem protótipo herdado do site: nada do host atravessa por referência.
  return { event: name, payload: JSON.parse(json) as Record<string, unknown> };
}
